import { useEffect } from 'react';
import { env } from '@/lib/env';

export type TranslateFn = (key: string, vars?: Record<string, unknown>) => string;

export type UseMediaPollingParams = {
    projectId?: string | null;
    nodeId: string;
    data: any;
    modelId: string;
    updateNodeData: (id: string, partial: any) => void;
    getNode: (id: string) => any;
    t: TranslateFn;
};

export type PollingResponseContext = {
    getLatestData: () => any;
    nodeId: string;
    modelId: string;
    projectId: string;
    updateNodeData: (id: string, partial: any) => void;
    t: TranslateFn;
};

export type UseGenerationPollingParams = UseMediaPollingParams & {
    statusEndpoint: string;
    getPollInterval?: (genStartMs: number) => number;
    handleResponse: (response: any, ctx: PollingResponseContext) => 'break' | 'continue';
};

const POLL_INTERVAL_1MIN = () => 60_000;
const POLL_INTERVAL_5MIN = () => 5 * 60_000;

/*
nodeId 兜底启动门槛(ms)：节点处于 processing 但缺少 processingGenerationId 时，
仅在「已 processing 超过该时长」后才用 projectId+nodeId 去查状态恢复。
- 刷新后卡死的节点：updatedAt 是几分钟前 → 立即触发，自动恢复。
- 新发起的生成：updatedAt≈现在 → 先等待，期间 202 会写入 processingGenerationId，
    effect 重跑切到 generationId 正常轮询，从而避免在新记录入库前误读到上一条 completed。
该判定只用客户端时钟（node.updatedAt 也由客户端写入），无跨时钟比较问题。
*/
const NODE_FALLBACK_MIN_AGE_MS = 30_000;

export function useGenerationPolling({
    projectId,
    nodeId,
    data,
    modelId,
    updateNodeData,
    getNode,
    t,
    statusEndpoint,
    getPollInterval = POLL_INTERVAL_1MIN,
    handleResponse,
}: UseGenerationPollingParams) {
    useEffect(() => {
        const ac = new AbortController();

        async function poll() {
            try {
                if (!projectId || data?.status !== 'processing') return;
                if (String(env.NEXT_PUBLIC_ENABLE_POLLING ?? 'true') === 'false') return;

                const processingGenerationId = data?.processingGenerationId as string | undefined;
                // 兜底模式：节点 processing 但缺 processingGenerationId（如 202 前刷新、
                // 或服务端写入被客户端保存覆盖丢失）→ 改用 projectId+nodeId 查最新记录恢复。
                const useNodeFallback = !processingGenerationId;

                const getLatestData = () => {
                    try { return getNode(nodeId)?.data; }
                    catch { return undefined; }
                };

                const abortableSleep = (ms: number) =>
                    new Promise<void>((resolve) => {
                        if (ac.signal.aborted) { resolve(); return; }
                        const timer = setTimeout(resolve, ms);
                        ac.signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
                    });

                const pollUrl = useNodeFallback
                    ? `${statusEndpoint}?projectId=${encodeURIComponent(projectId)}&nodeId=${encodeURIComponent(nodeId)}`
                    : `${statusEndpoint}?generationId=${encodeURIComponent(processingGenerationId!)}`;

                // 兜底模式启动门槛：节点 processing 未满 NODE_FALLBACK_MIN_AGE_MS 时先等待，
                // 给新发起的生成留出写入 processingGenerationId 的时间，避免误读旧记录。
                if (useNodeFallback) {
                    const updatedAtMs = Date.parse(String(data?.updatedAt ?? '')) || 0;
                    const age = updatedAtMs ? Date.now() - updatedAtMs : Number.POSITIVE_INFINITY;
                    if (age < NODE_FALLBACK_MIN_AGE_MS) {
                        await abortableSleep(NODE_FALLBACK_MIN_AGE_MS - age);
                        if (ac.signal.aborted) return;
                        // 等待期间若已拿到 processingGenerationId，则交给重跑后的 generationId 路径处理。
                        if (getLatestData()?.processingGenerationId) return;
                        if (getLatestData()?.status !== 'processing') return;
                    }
                }

                const ctx: PollingResponseContext = {
                    getLatestData, nodeId, modelId, projectId, updateNodeData, t,
                };

                while (!ac.signal.aborted) {
                    try {
                        const st = await fetch(pollUrl, { method: 'GET', signal: ac.signal, cache: 'no-store' });
                        if (ac.signal.aborted) break;

                        if (st.ok) {
                            const result = handleResponse(await st.json(), ctx);
                            if (result === 'break') break;
                        } else if (st.status === 404) {
                            // 兜底模式下查无记录：不武断置为 failed（避免把刚发起、记录尚未入库的
                            // 生成误判为失败并同步给协作方），继续等待下一轮。
                            if (!useNodeFallback) {
                                try {
                                    updateNodeData(nodeId, {
                                        status: 'failed',
                                        updatedAt: new Date().toISOString(),
                                        error: 'Generation record not found',
                                        processingGenerationId: undefined,
                                        runtimeEstimatedTime: undefined,
                                    });
                                } catch {}
                                break;
                            }
                        }
                    } catch (e) {
                        if ((e as Error).name === 'AbortError') break;
                    }

                    const latest = getLatestData();
                    if (latest?.status !== 'processing') break;
                    if (useNodeFallback) {
                        // 兜底途中拿到了 processingGenerationId → 退出，由重跑后的 generationId 路径接管。
                        if (latest?.processingGenerationId) break;
                    } else {
                        const curPid = latest?.processingGenerationId;
                        if (!curPid || curPid !== processingGenerationId) break;
                    }
                    await abortableSleep(getPollInterval(Date.now()));
                }
            } catch {}
        }

        poll();
        return () => ac.abort();
    }, [
        projectId,
        nodeId,
        data?.status,
        data?.processingGenerationId,
        modelId,
        updateNodeData,
    ]);
}